import type { Person, Question } from 'talent_matching/types';

// Field types to exclude from table display
const EXCLUDED_VALUE_TYPES = ['long_text', 'rich_text'];
const EXCLUDED_QUESTION_TEXTS = ['linkedin profile', 'website'];

export type QuestionColumn = {
  field: string;
  label: string;
  description?: string;
  question: string;
};

const isDisplayableQuestion = (question: Question): boolean => {
  const normalizedQuestion = question.question_label.trim().toLowerCase();
  if (EXCLUDED_QUESTION_TEXTS.includes(normalizedQuestion)) {
    return false;
  }

  if (!question.value_type) {
    return true;
  }

  return !EXCLUDED_VALUE_TYPES.includes(question.value_type);
};

const getCandidateQuestions = (candidate: Person): Question[] => {
  const {
    applicationQuestions = [],
    agencyQuestions = [],
    referralDetails = [],
  } = candidate;

  return [...applicationQuestions, ...agencyQuestions, ...referralDetails];
};

const stableHash = (value: string): string => {
  // FNV-1a 32-bit hash for stable, deterministic ids
  // eslint-disable-next-line no-bitwise
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  // eslint-disable-next-line no-bitwise
  return (hash >>> 0).toString(36);
};

export const buildQuestionColumnField = (questionText: string): string => {
  const normalized = questionText.trim();
  const hash = stableHash(normalized);
  const slug = normalized
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30);

  return `qa_${hash}_${slug || 'question'}`;
};

export const buildQuestionColumnsFromPeople = (
  people: Person[]
): QuestionColumn[] => {
  const seen = new Set<string>();
  const columns: QuestionColumn[] = [];

  people.forEach((person) => {
    const displayableQuestions = getCandidateQuestions(person).filter(
      isDisplayableQuestion
    );

    displayableQuestions.forEach((q) => {
      const question = q.question.trim();
      if (question.length === 0 || seen.has(question)) {
        return;
      }

      const label = q.question_label.trim();
      const description = q.question_description?.trim();

      seen.add(question);
      columns.push({
        field: buildQuestionColumnField(question),
        label,
        description,
        question,
      });
    });
  });

  return columns;
};

export const getAnswerForQuestion = (
  candidate: Person,
  questionText: string
): string | null => {
  const normalizedQuestion = questionText.trim();
  const question = getCandidateQuestions(candidate)
    .filter(isDisplayableQuestion)
    .find((q) => q.question.trim() === normalizedQuestion);

  return question?.answer ?? null;
};
